/* eslint-disable react/prop-types */
import { useState, useEffect } from 'react';
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import DashboardAdmin from "./pages/DashboardAdmin/DashboardAdmin.jsx";
import ListaClientes from "./pages/Clientes/ListaClientes.jsx";
import AdminPanelProductos from "../Store/AdminPanelProductos.jsx";
import ListaModelosLlaverosTodosAdmin from "../Profile/pages/Modelos/ListaModelosLlaverosTodosAdmin.jsx";
import EnlacesPublicosAdmin from "../Profile/pages/Admin/EnlacesPublicos/EnlacesPublicosAdmin.jsx";
import { useAuthContext } from "../../components/providers/AuthProvider.jsx";
import AdminCustomOrderRequests from "./pages/CustomOrderRequests/AdminCustomOrderRequests.jsx";
import AdminLeads from "./pages/Leads/AdminLeads.jsx";
import AdminOrders from "./pages/Orders/AdminOrders.jsx";
import AdminUsers from "./pages/Users/AdminUsers.jsx";
import DigitalProductsAdmin from "./DigitalProductsAdmin.jsx";
import LegalVersionsAdmin from "./pages/Legal/LegalVersionsAdmin.jsx";
import EnviosAdmin from "./pages/Envios/EnviosAdmin.jsx";
import EntitlementsAdmin from "./pages/Entitlements/EntitlementsAdmin.jsx";
import CommunicationsAdmin from "./pages/Communications/CommunicationsAdmin.jsx";
import ProduccionAdmin from "./pages/Produccion/ProduccionAdmin.jsx";
import RolesPermisosAdmin from "./pages/Roles/RolesPermisosAdmin.jsx";
import AdminProyectos from "./pages/Proyectos/AdminProyectos.jsx";
import AdminApiTokens from "./pages/ApiTokens/AdminApiTokens.jsx";
import { canManageProjects } from "../../lib/permissions.js";

// Componentes mapeados
const componentMap = {
    'general': DashboardAdmin,
    'leads': AdminLeads,
    'proyectos': AdminProyectos,
    'tokens_api': AdminApiTokens,
    'clientes': ListaClientes,
    'usuarios': AdminUsers,
    'productos': AdminPanelProductos,
    'pedidos': AdminOrders,
    'todo_modelos_creados': ListaModelosLlaverosTodosAdmin,
    'enlaces_publicos': EnlacesPublicosAdmin,
    'solicitudes': AdminCustomOrderRequests,
    'producto_digital': DigitalProductsAdmin,
    'entitlements': EntitlementsAdmin,
    'communications': CommunicationsAdmin,
    'legal': LegalVersionsAdmin,
    'envios': EnviosAdmin,
    'produccion': ProduccionAdmin,
    'roles': RolesPermisosAdmin,
};

// Rutas que exigen, además de `role === "admin"`, un permiso Django concreto.
// No basta con ocultar el enlace del sidebar: entrar por URL directa a una
// ruta sin el permiso debe fallar de forma visible aquí también, no solo
// cuando la API responda 403 a cada petición.
const ROUTE_PERMISSIONS = {
    envios:        'store.manage_shipping_config',
    entitlements:  'CustomizedKeyring.manage_digital_entitlement',
    communications: 'communications.manage_communication',
    produccion:    'fulfillment.manage_filament_references',
};

// Secciones que también abre el rol comercial (C3D-38): las del CRM. El
// backend le enseña solo lo que tiene asignado.
const PROJECT_ROUTES = new Set(['proyectos', 'leads', 'clientes', 'tokens_api']);

// Hook personalizado para detectar pantallas grandes
const useIsLargeScreen = () => {
    const [isLargeScreen, setIsLargeScreen] = useState(
        typeof window !== 'undefined' ? window.innerWidth >= 1024 : false
    );

    useEffect(() => {
        const handleResize = () => {
            setIsLargeScreen(window.innerWidth >= 1024);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return isLargeScreen;
};

const AnimatedPage = ({ children, routeKey, isVisible, onExitComplete }) => {
    const isLargeScreen = useIsLargeScreen();

    return (
        <motion.div
            key={routeKey}
            initial={{
                opacity: 0,
                x: isLargeScreen ? 0 : 100,
                y: isLargeScreen ? -100 : 0
            }}
            animate={{
                opacity: isVisible ? 1 : 0,
                x: isVisible ? 0 : (isLargeScreen ? 0 : -100),
                y: isVisible ? 0 : (isLargeScreen ? 100 : 0)
            }}
            exit={{
                opacity: 0,
                x: isLargeScreen ? 0 : -100,
                y: isLargeScreen ? 100 : 0
            }}
            transition={{
                duration: 0.4,
                ease: "easeInOut"
            }}
            className="w-full h-full absolute inset-0 overflow-auto"
            style={{
                pointerEvents: isVisible ? 'auto' : 'none',
                zIndex: isVisible ? 1 : 0
            }}
            onAnimationComplete={(definition) => {
                // Si la animación que acaba de completarse es una animación de salida
                if (definition.opacity === 0 && onExitComplete) {
                    console.log('AdminRoutes exit animation completed');
                    onExitComplete();
                }
            }}
        >
            {children}
        </motion.div>
    );
};

export default function AdminRoutes({ onRouteChangeStart, onRouteChangeComplete }) {
    const authContext = useAuthContext();
    const location = useLocation();
    const [currentRoute, setCurrentRoute] = useState('general');
    const [isExiting, setIsExiting] = useState(false);
    const [nextRoute, setNextRoute] = useState(null);

    // Extraer ruta del pathname
    const getRouteFromPath = (pathname) => {
        const pathSegments = pathname.split('/').filter(Boolean);
        return pathSegments[pathSegments.length - 1] || 'general';
    };

    // Escuchar cambios de ruta
    useEffect(() => {
        const newRoute = getRouteFromPath(location.pathname);
        if (newRoute !== currentRoute && !isExiting) {
            console.log('Route change detected, updating current route to:', newRoute);
            setCurrentRoute(newRoute);
        }
    }, [location.pathname, currentRoute, isExiting]);

    // Manejar inicio de cambio de ruta desde el menú
    useEffect(() => {
        if (onRouteChangeStart) {
            // Función que será llamada por el menú cuando inicie una animación de salida
            window.adminRoutesChangeHandler = (newRoute) => {
                console.log('AdminRoutes: Starting exit animation for route change to:', newRoute);
                setIsExiting(true);
                setNextRoute(newRoute);
            };
        }

        return () => {
            delete window.adminRoutesChangeHandler;
        };
    }, [onRouteChangeStart]);

    // Manejar finalización de animación de salida
    const handleExitComplete = () => {
        console.log('AdminRoutes: Exit animation completed, updating to next route:', nextRoute);
        if (nextRoute) {
            setCurrentRoute(nextRoute);
            setNextRoute(null);
        }
        setIsExiting(false);

        if (onRouteChangeComplete) {
            onRouteChangeComplete();
        }
    };

    const renderComponent = (route) => {
        const Component = componentMap[route];
        const requiredPermission = ROUTE_PERMISSIONS[route];

        const allowed = authContext.role === "admin"
            || (PROJECT_ROUTES.has(route) && canManageProjects(authContext));
        if (Component && allowed) {
            if (requiredPermission && !authContext.permissions?.includes(requiredPermission)) {
                return (
                    <div className="flex items-center justify-center h-full">
                        <div className="text-center max-w-md px-4">
                            <h2 className="text-2xl font-semibold mb-2">No autorizado</h2>
                            <p className="text-gray-600">
                                Tu cuenta no tiene el permiso necesario para esta sección
                                ({requiredPermission}). Pide que te lo concedan si lo necesitas.
                            </p>
                        </div>
                    </div>
                );
            }
            return <Component />;
        }

        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-center">
                    <h2 className="text-2xl font-semibold mb-2">Seleccione una opción del menú</h2>
                    <p className="text-gray-600">Elige una sección del panel de administración</p>
                    <p className="text-xs text-gray-400 mt-2">Ruta actual: {route}</p>
                </div>
            </div>
        );
    };
    return (
        <div className="w-full h-full relative overflow-hidden">
            <AnimatePresence mode="wait" initial={false}>
                <AnimatedPage
                    routeKey={currentRoute}
                    isVisible={!isExiting}
                    onExitComplete={handleExitComplete}
                >
                    {renderComponent(currentRoute)}
                </AnimatedPage>
            </AnimatePresence>
        </div>
    );
}
